<script setup lang="ts">
import Badge from '@/components/ui/Badge.vue';
import { BadgeCheck, Database, FileText, Lock, ScanLine, ShieldCheck } from 'lucide-vue-next';

interface Step {
    icon: any;
    title: string;
    description: string;
}

const steps: Step[] = [
    {
        icon: ScanLine,
        title: 'Capture asset details',
        description: 'Record the information needed to identify and describe selected banknotes or cash holdings.',
    },
    {
        icon: FileText,
        title: 'Create a certificate',
        description: 'Generate a structured digital document that can be stored, shared and checked when verification is needed.',
    },
    {
        icon: Database,
        title: 'Anchor the record',
        description: 'Use blockchain-backed transparency to make the certificate history easier to verify and harder to manipulate.',
    },
    {
        icon: BadgeCheck,
        title: 'Verify ownership',
        description: 'Support reporting, internal checks and asset confirmation with consistent digital documentation.',
    },
];
</script>

<template>
    <section id="whitepaper" class="py-20">
        <div class="container mx-auto px-4">
            <div class="mb-16 text-center">
                <Badge variant="secondary" class="mb-4">Technology</Badge>
                <h2 class="mb-6">From physical banknote to verifiable digital record</h2>
                <p class="mx-auto max-w-4xl text-xl leading-relaxed text-muted-foreground">
                    Digital Safe combines asset registration, certificate generation and blockchain-backed verification into one practical workflow.
                </p>
            </div>

            <div class="glass-card glow-hover mb-16">
                <div class="grid grid-cols-1 gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
                    <div>
                        <div class="glow mb-6 flex h-14 w-14 items-center justify-center rounded-lg bg-gradient-hero">
                            <ShieldCheck class="h-7 w-7 text-primary-foreground" />
                        </div>
                        <h3 class="mb-4 text-2xl font-bold text-primary">Built for proof, not promises</h3>
                        <p class="leading-relaxed text-muted-foreground">
                            The platform is designed around practical verification: what was registered, when it was certified and how the record can
                            be checked later by the owner, an organization or another trusted party.
                        </p>
                    </div>
                    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div class="rounded-lg border border-glass-border bg-secondary/30 p-5">
                            <Lock class="mb-4 h-6 w-6 text-primary" />
                            <h4 class="mb-2 font-semibold">Controlled access</h4>
                            <p class="text-sm text-muted-foreground">Keep sensitive asset data structured and available only where it is needed.</p>
                        </div>
                        <div class="rounded-lg border border-glass-border bg-secondary/30 p-5">
                            <Database class="mb-4 h-6 w-6 text-primary" />
                            <h4 class="mb-2 font-semibold">Transparent record</h4>
                            <p class="text-sm text-muted-foreground">Use traceable records to support authenticity and historical verification.</p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                <div v-for="(step, index) in steps" :key="step.title" class="glass-card glow-hover">
                    <div class="mb-5 flex items-center justify-between">
                        <div class="glow flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                            <component :is="step.icon" class="h-6 w-6 text-primary-foreground" />
                        </div>
                        <span class="text-sm font-semibold text-primary">0{{ index + 1 }}</span>
                    </div>
                    <h3 class="mb-3 text-xl font-bold">{{ step.title }}</h3>
                    <p class="text-sm leading-relaxed text-muted-foreground">{{ step.description }}</p>
                </div>
            </div>
        </div>
    </section>
</template>
